import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import { useRecordVisitRate, useVisitRates } from '../api/policies.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { Staleness } from '../components/Staleness.tsx';
import { formatDate } from '../dates.ts';
import type { VisitRate, VisitRatePolicies } from '../api/types.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';

function describe(rate: VisitRate): string {
  return `${rate.visits_per_year} a year · every ${rate.interval_days} days · ${rate.grace_days} days’ grace`;
}

/**
 * Company policies, tier 1 only: visits per year by residence type.
 *
 * **A rate on record is never changed.** Recording a new rate inserts it beside
 * the old ones with its own effective date, so every figure for a past period
 * keeps the standard that applied in it. There is no edit or delete control on
 * an existing rate, and the server has no route for either.
 *
 * The entry is not in anyone else's menu, and the page says so plainly if
 * reached by address; the server refuses regardless.
 */
export function CompanyPolicies() {
  const mine = useMyDashboards();
  const tierOne = mine.data?.tier === 1;

  return (
    <div className="min-h-dvh bg-neutral-50">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="border-b border-neutral-200 bg-white px-4 pt-3 pb-3 lg:rounded-t-lg lg:px-5 lg:pt-4 lg:shadow">
          <h1 className="text-xl font-bold text-neutral-900">Company policies</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Agency-wide standards. Each change is recorded from its effective date; standards already on
            record stay as they were, so figures for past periods keep the standard that applied then.
          </p>
        </header>
        <div className="bg-white lg:rounded-b-lg lg:shadow">
          {mine.error && !mine.data ? (
            <LoadFailure what="Company policies" error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : mine.isPending ? (
            <p className="px-4 py-10 text-center text-neutral-500">Loading…</p>
          ) : !tierOne ? (
            <div role="alert" className="p-6">
              <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
              <p className="mt-2 text-neutral-700">Company policies are open to tier 1 only.</p>
              <Link
                to="/clients"
                className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5 font-semibold text-white"
              >
                Back to clients
              </Link>
            </div>
          ) : (
            <Policies />
          )}
        </div>
      </div>
    </div>
  );
}

function Policies() {
  const rates = useVisitRates(true);
  const [opened, setOpened] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<string | null>(null);

  if (rates.error && !rates.data) {
    return (
      <LoadFailure
        what="Visit rates"
        error={rates.error}
        onRetry={() => void rates.refetch()}
        retrying={rates.isFetching}
        forbiddenIsSession={false}
      />
    );
  }
  if (!rates.data) return <p className="px-4 py-10 text-center text-neutral-500">Loading visit rates…</p>;

  const data = rates.data;
  const history = data.types
    .flatMap((t) => [...t.scheduled, ...(t.current ? [t.current] : []), ...t.earlier].map((r) => ({ type: t, rate: r })))
    .sort((a, b) =>
      b.rate.effective_from.localeCompare(a.rate.effective_from) ||
      a.type.label.localeCompare(b.type.label));

  return (
    <>
      <Staleness
        updatedAt={rates.dataUpdatedAt}
        isFetching={rates.isFetching}
        error={rates.error}
        onRefresh={() => void rates.refetch()}
      />

      <section className="px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">Visits per year, by residence type</h2>
        <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-600">
          How often a client must be seen, by the kind of place they live in. A court order can raise it
          for one client; nothing here lowers what a court ordered.
        </p>
        {recorded && (
          <p role="status" className="mt-3 rounded-lg bg-note-bg px-3 py-2 text-[0.9375rem] text-note-fg ring-1 ring-note-edge">
            {recorded}
          </p>
        )}

        <ul className="mt-3 divide-y divide-neutral-200 rounded-lg border border-neutral-200">
          {data.types.map((type) => (
            <li key={type.code} className="px-3 py-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-neutral-900">{type.label}</p>
                  <p className="text-[0.9375rem] text-neutral-700">
                    {type.current
                      ? <>{describe(type.current)} <span className="text-neutral-500">· since {formatDate(type.current.effective_from)}</span></>
                      : 'No rate in force today'}
                  </p>
                  {type.scheduled.map((s) => (
                    <p key={s.id} className="text-sm text-neutral-600">
                      Then {describe(s)} from {formatDate(s.effective_from)}
                    </p>
                  ))}
                </div>
                {opened !== type.code && (
                  <button
                    type="button"
                    onClick={() => { setOpened(type.code); setRecorded(null); }}
                    className="min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-800
                               hover:bg-neutral-50"
                  >
                    Record a new rate
                  </button>
                )}
              </div>
              {opened === type.code && (
                <NewRateForm
                  type={type}
                  today={data.today}
                  onCancel={() => setOpened(null)}
                  onRecorded={(message) => { setOpened(null); setRecorded(message); }}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">History</h2>
        <p className="mt-0.5 text-sm text-neutral-600">Every visit rate on record, newest effective date first.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-[0.9375rem]">
            <thead className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
              <tr>
                <th className="py-2 pr-4">Residence type</th>
                <th className="py-2 pr-4">Rate</th>
                <th className="py-2 pr-4">Effective from</th>
                <th className="py-2 pr-4">Policy</th>
                <th className="py-2">Recorded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {history.map(({ type, rate }) => (
                <tr key={rate.id} className="align-top">
                  <td className="py-2 pr-4 text-neutral-900">{type.label}</td>
                  <td className="py-2 pr-4 text-neutral-800">{describe(rate)}</td>
                  <td className="py-2 pr-4 tabular-nums text-neutral-800">
                    {formatDate(rate.effective_from)}
                    {rate.effective_from > data.today && <span className="block text-xs text-neutral-500">scheduled</span>}
                  </td>
                  <td className="py-2 pr-4 text-neutral-700">
                    {rate.policy_name ?? '—'}
                    {rate.adopted_reason && <span className="block max-w-[32rem] text-sm text-neutral-500">{rate.adopted_reason}</span>}
                  </td>
                  {/* created_at is an instant, so converting it to the viewer's zone is correct. */}
                  <td className="py-2 tabular-nums text-neutral-600">{new Date(rate.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">Hospitalisation check-ins</h2>
        <p className="mt-0.5 text-[0.9375rem] text-neutral-600">
          How often a client in hospital is checked on will be configurable here.
        </p>
      </section>
    </>
  );
}

function NewRateForm({
  type,
  today,
  onCancel,
  onRecorded,
}: {
  type: VisitRatePolicies['types'][number];
  today: string;
  onCancel: () => void;
  onRecorded: (message: string) => void;
}) {
  const record = useRecordVisitRate();
  const base = type.current;
  // Empty, not 4 / 91 / 14.
  //
  // This was the one place in the application that INVENTED a cadence. A type
  // with a rate in force is pre-filled from it, which is right — most changes
  // are an adjustment to what is there. A type with none had the current
  // agency policy typed into the form as though it had been chosen, and
  // nothing on screen said it was a guess; pressing Record would have written
  // today's cadence into `visit_target` as a decision nobody made.
  //
  // Blank fields make the person state the rate. That is the whole point of
  // this screen.
  const [visits, setVisits] = useState(base ? String(base.visits_per_year) : '');
  const [interval, setInterval] = useState(base ? String(base.interval_days) : '');
  const [grace, setGrace] = useState(base ? String(base.grace_days) : '');
  const [from, setFrom] = useState(today);
  const [policy, setPolicy] = useState(base?.policy_name ?? '');
  const [reason, setReason] = useState('');

  const perYear = Number(visits);
  // Each field must have been TYPED. Number('') is 0, which used to satisfy
  // `grace >= 0` — so a blank grace box submitted a zero nobody chose. With
  // the boxes no longer pre-filled that is the difference between "no grace"
  // and "not stated".
  const stated = (s: string) => s.trim() !== '' && Number.isInteger(Number(s));
  const valid =
    stated(visits) && perYear >= 1 && perYear <= 365 &&
    stated(interval) && Number(interval) >= 1 &&
    stated(grace) && Number(grace) >= 0 &&
    from >= today;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    record.mutate(
      {
        residence_type_code: type.code,
        visits_per_year: perYear,
        interval_days: Number(interval),
        grace_days: Number(grace),
        effective_from: from,
        policy_name: policy.trim() || null,
        adopted_reason: reason.trim() || null,
      },
      {
        onSuccess: (res) =>
          onRecorded(`${res.residence_type_label}: ${describe(res.rate)} from ${formatDate(res.rate.effective_from)}, recorded.`),
      },
    );
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg bg-neutral-50 p-3">
      <p className="text-sm text-neutral-700">
        A new rate for <span className="font-semibold">{type.label}</span>, recorded beside the current one.
        The current rate stays on record and stays in force until the new one takes effect.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className={label} htmlFor={`rate-visits-${type.code}`}>Visits per year</label>
          <input id={`rate-visits-${type.code}`} type="number" min={1} max={365} step={1} required
                 value={visits} onChange={(e) => setVisits(e.target.value)} className={field} />
        </div>
        <div>
          <label className={label} htmlFor={`rate-interval-${type.code}`}>Longest gap between visits (days)</label>
          <input id={`rate-interval-${type.code}`} type="number" min={1} step={1} required
                 value={interval} onChange={(e) => setInterval(e.target.value)} className={field} />
          {perYear >= 1 && (
            <p className="mt-1 text-xs text-neutral-500">
              At {perYear} a year, visits fall about {Math.round(365 / perYear)} days apart.
            </p>
          )}
        </div>
        <div>
          <label className={label} htmlFor={`rate-grace-${type.code}`}>Grace (days)</label>
          <input id={`rate-grace-${type.code}`} type="number" min={0} step={1} required
                 value={grace} onChange={(e) => setGrace(e.target.value)} className={field} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`rate-from-${type.code}`}>Takes effect</label>
          <input id={`rate-from-${type.code}`} type="date" min={today} required
                 value={from} onChange={(e) => setFrom(e.target.value)} className={field} />
          <p className="mt-1 text-xs text-neutral-500">Today or later. An earlier date would restate past figures.</p>
        </div>
        <div>
          <label className={label} htmlFor={`rate-policy-${type.code}`}>Policy name</label>
          <input id={`rate-policy-${type.code}`} maxLength={48}
                 value={policy} onChange={(e) => setPolicy(e.target.value)} className={field} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor={`rate-reason-${type.code}`}>Why it changed</label>
        <textarea id={`rate-reason-${type.code}`} rows={2} maxLength={2000}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-base text-neutral-900" />
      </div>

      {record.error && (
        <div role="alert" className="rounded-lg border-l-8 border-critical-edge bg-critical-bg px-4 py-3 text-critical-fg">
          <p className="font-semibold">{record.error.message}</p>
          {record.error instanceof ApiError && record.error.hint && (
            <p className="mt-1 text-[0.9375rem] opacity-95">{record.error.hint}</p>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!valid || record.isPending}
          className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:opacity-50"
        >
          {record.isPending ? 'Recording…' : 'Record rate'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
